<template>
    <validation-observer
        :ref="initialValues ? 'edit_form' : 'create_form'"
        v-slot="{ handleSubmit, meta, setFieldValue, values }"
    >
        <modal
            :id="modalId"
            :title="initialValues ? 'Edit Template' : 'Create Template'"
            @ok.prevent="handleSubmit(onSubmit)"
            @shown="setTemplateData(isEdit ? 'edit_form' : 'create_form')"
            @hidden="clearTemplateData(isEdit ? 'edit_form' : 'create_form')"
            :ok-disabled="!meta.valid"
            size="lg"
        >
            <validation-provider validate-on-input v-slot="{ errors, field }" name="name" rules="required">
                <form-input
                    floatingLabel="Template name"
                    class="template-name-input"
                    v-bind="field"
                    :error="errors[0]"
                />
            </validation-provider>
            <validation-provider
                v-if="userIsAdmin"
                validate-on-input
                v-slot="{ errors, field }"
                name="template_group"
                rules="required"
            >
                <form-select
                    floatingLabel="Template Group"
                    v-bind="field"
                    :options="templateGroups"
                    :error="errors[0]"
                    @change="(val) => setFieldValue('template_type', templateTypes[val][0].value)"
                />
            </validation-provider>
            <validation-provider
                v-if="userIsAdmin"
                validate-on-input
                v-slot="{ errors, field }"
                name="template_type"
                rules="required"
            >
                <form-select
                    floatingLabel="Template type"
                    v-bind="field"
                    :options="templateTypes[values.template_group]"
                    :error="errors[0]"
                />
            </validation-provider>
            <validation-provider v-if="!template.is_default" name="is_rental_agreement">
                <form-checkbox
                    v-model="template.is_rental_agreement"
                    @change="(val) => setFieldValue('is_rental_agreement', val)"
                    :disabled="isEdit"
                    >Rental Agreement?</form-checkbox
                >
            </validation-provider>
            <validation-provider name="is_default_rental_agreement">
                <form-checkbox
                    v-model="template.is_default_rental_agreement"
                    @change="(val) => setFieldValue('is_default_rental_agreement', val)"
                    class="mr-2"
                    :disabled="!template.is_rental_agreement"
                >
                    Make this template the default rental agreement?
                </form-checkbox>
            </validation-provider>
            <validation-provider name="use_as_pdf">
                <form-checkbox :disabled="template.is_rental_agreement" :value="template.is_rental_agreement ? true : values.use_as_pdf" @input="(val) => setFieldValue('use_as_pdf', val)"
                    >Make this template available as a PDF attachment to recipient?</form-checkbox
                >
            </validation-provider>
            <validation-provider v-if="!isEdit" v-slot="{ value }" name="template_id" rules="required">
                <div class="mt-2 mb-2">Select design</div>
                <div class="templates-list">
                    <div
                        v-for="template in predefinedTemplates"
                        :key="template.id"
                        class="templates-list-item"
                        @click="setFieldValue('template_id', template.id)"
                        :class="{ 'templates-list-item-active': template.id === value }"
                    >
                        <p>{{ template.title }}</p>
                        <img :src="template.image" alt="Template design" />
                    </div>
                </div>
            </validation-provider>
        </modal>
    </validation-observer>
</template>

<script>
import { mapGetters } from 'vuex';
import FormInput from '../../generic/form-input/form-input.vue';
import formSelect from '../../generic/form-select/form-select.vue';
import FormCheckbox from '../../generic/form-checkbox/form-checkbox.vue';
import { getTemplateTypeLabel, TEMPLATE_GROUPS, GROUPPED_TEMPLATE_TYPES, TEMPLATE_TYPES } from './constants';
import Modal from '../../generic/modal/modal.vue';
export default {
    components: {
        FormInput,
        formSelect,
        Modal,
        FormCheckbox,
    },
    name: 'UnlayerTemplatesCreateModal',
    props: {
        predefinedTemplates: {
            type: Array,
            required: true,
        },
        initialValues: {
            type: Object,
            required: false,
        },
        modalId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            template: {
                name: '',
                template_group: 'CUSTOM',
                template_type: 'USER',
                id: '',
                is_rental_agreement: false,
                use_as_pdf: false,
            },
        };
    },

    computed: {
        ...mapGetters('header', ['userIsAdmin']),
        isEdit() {
            return !!this.initialValues;
        },
        templateGroups() {
            const groups = Object.keys(TEMPLATE_GROUPS).map((group) => ({
                value: group,
                text: getTemplateTypeLabel(group),
            }));
            if (!this.userIsAdmin) {
                return groups.filter((group) => [TEMPLATE_GROUPS.CUSTOM, TEMPLATE_GROUPS.GUEST].includes(group.value));
            }
            return groups;
        },
        templateTypes() {
            const types = GROUPPED_TEMPLATE_TYPES;
            if (!this.userIsAdmin) {
                const allowedGroups = [TEMPLATE_GROUPS.CUSTOM, TEMPLATE_GROUPS.GUEST];
                const allowedTypes = [TEMPLATE_TYPES.USER, TEMPLATE_TYPES.RENTAL_AGREEMENT];
                const filteredTypes = Object.fromEntries(
                    allowedGroups
                        .filter((group) => types[group])
                        .map((group) => [group, types[group].filter((type) => allowedTypes.includes(type))])
                );

                return filteredTypes;
            }
            return types;
        },
        showSetAsDefaultRentalAgreement() {
            return this.template.template_type === TEMPLATE_TYPES.RENTAL_AGREEMENT.value;
        },
    },
    methods: {
        setTemplateData(field) {
            this.$refs[field].setValues({
                ...this.template,
            });
        },
        clearTemplateData(field) {
            this.$refs[field].setValues({
                name: '',
                template_group: 'CUSTOM',
                template_type: 'USER',
                id: '',
                is_rental_agreement: false,
                use_as_pdf: false,
            });
        },
        onSubmit(values) {
            this.$emit('submit', values);
        },
    },
    watch: {
        initialValues: {
            handler(newVal) {
                this.template = {
                    ...newVal,
                    is_rental_agreement: newVal.template_type === TEMPLATE_TYPES.RENTAL_AGREEMENT.value,
                };
            },
        },
    },
};
</script>

<style lang="scss" scoped>
.templates-list {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.templates-list-item {
    width: 250px;
    height: 200px;
    border: 1px solid #e0e0e0;
    border-radius: 5px;
    padding: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        overflow: hidden;
    }
    &:hover,
    &-active {
        border-color: rgb(12, 72, 161);
        background: rgb(12, 72, 161);
        color: #fff;
    }
}
</style>
